import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';

import { ReactComponent as EmailIcon } from 'assets/images/email-inbound.svg';
import CopyMessage from './CopyMessage';
import { CommunicationDisplay, Email, Colors } from 'typings';

interface EmailProps extends CommunicationDisplay {
  data: Email;
  children: React.ReactNode;
  caption?: React.ReactElement;
}

const useStyles = makeStyles((theme: Theme) => ({
  innerWrapper: {
    width: '100%',
  },
  textWrapper: (props: EmailProps) => ({
    position: 'relative',
    width: '100%',
    display: 'inline-flex',
    flexDirection: 'column',
    backgroundColor: props.data.incoming
      ? theme.palette.background.custom
      : theme.palette.action.selected,
    boxShadow: `0px 3px 6px ${Colors.TRANSPARENT_BLACK}`,
    borderRight: props.data.incoming ? `5px solid ${Colors.DARK_PURPLE}` : 'inherit',
    borderLeft: !props.data.incoming ? `5px solid ${Colors.DARK_PURPLE}` : 'inherit',
    borderRadius: theme.spacing(0.75),
    padding: props.data.incoming ? theme.spacing(1, 1, 1, 3) : theme.spacing(1, 3, 1, 1),
  }),
  text: {
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': 4,
    '-webkit-box-orient': 'vertical',
  },
  captionWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  viewEmail: {
    fontWeight: 500,
    padding: theme.spacing(0.5, 1),
    color: theme.palette.grey['500'],
  },
  svg: (props: EmailProps) => ({
    position: 'absolute',
    top: 'calc(50% - 1rem)',
    left: props.data.incoming ? theme.spacing(-2) : 'auto',
    right: !props.data.incoming ? theme.spacing(-2) : 'auto',
    width: theme.spacing(4),
    borderRadius: '50%',
  }),
  email: {
    display: 'flex',
    padding: 0,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'inherit',
    },
    '& svg': {
      display: 'inline-flex',
      width: theme.spacing(2),
    },
  },
}));

const EmailContent = ({ data, children, caption }: React.PropsWithChildren<EmailProps>) => {
  const classes = useStyles({ data, children });
  const [inHover, setHover] = useState(false);

  const showCopyButton = inHover && data.incoming && !data.hasIssue;

  return (
    <>
      <Box
        className={classes.innerWrapper}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <Box className={classes.textWrapper}>
          <EmailIcon className={classes.svg} />
          <Typography variant="body2" className={classes.text}>
            {children}
          </Typography>
          {!!showCopyButton && <CopyMessage text={data.text} />}
        </Box>
      </Box>
      {caption}
    </>
  );
};

export default EmailContent;
